import { createContext, useContext, useState } from "react";
import { PenTool, Edit3, Calendar } from "lucide-react";

const SignatureContext = createContext(null);

export const FIELD_TYPES = {
  SIGNATURE: "signature",
  INITIAL: "initial",
  DATE: "date",
};

export const fieldConfig = {
  [FIELD_TYPES.SIGNATURE]: {
    label: "Signature",
    icon: PenTool,
    borderColor: "border-emerald-600",
    dashedBg: "bg-emerald-100",
    textColor: "text-emerald-700",
    activeBg: "bg-emerald-500",
    tagBg: "bg-emerald-100",
  },
  [FIELD_TYPES.INITIAL]: {
    label: "Initials",
    icon: Edit3,
    borderColor: "border-purple-600",
    dashedBg: "bg-purple-100",
    textColor: "text-purple-700",
    activeBg: "bg-purple-100",
    tagBg: "bg-purple-100",
  },
  [FIELD_TYPES.DATE]: {
    label: "Date",
    icon: Calendar,
    borderColor: "border-amber-600",
    dashedBg: "bg-amber-100",
    textColor: "text-amber-700",
    activeBg: "bg-amber-100",
    tagBg: "bg-amber-100",
  },
};

const initialState = {
  pdfFile: null,
  blocks: [],
  signatureImage: null,
  currentStep: 1,
  signatures: {},
  activeFieldType: FIELD_TYPES.SIGNATURE,
  signerName: "",
  savedSignature: null,
  savedInitial: null,
};

export function SignatureProvider({ children }) {
  const [pdfFile, setPdfFileRaw] = useState(initialState.pdfFile);
  const [blocks, setBlocks] = useState(initialState.blocks);
  const [signatureImage, setSignatureImage] = useState(
    initialState.signatureImage,
  );
  const [currentStep, setCurrentStep] = useState(initialState.currentStep);
  const [signatures, setSignatures] = useState(initialState.signatures);
  const [activeFieldType, setActiveFieldType] = useState(
    initialState.activeFieldType,
  );
  const [signerName, setSignerName] = useState(initialState.signerName);
  const [savedSignature, setSavedSignature] = useState(initialState.savedSignature);
  const [savedInitial, setSavedInitial] = useState(initialState.savedInitial);

  // Wrap setPdfFile — whenever a new PDF is loaded, treat it as a new request
  // and clear any carried-over saved signature/initials + field state.
  const setPdfFile = (file) => {
    setPdfFileRaw(file);
    if (file) {
      setSavedSignature(null);
      setSavedInitial(null);
      setSignatures({});
    }
  };

  const resetSignatureContext = () => {
    setPdfFile(null);
    setBlocks([]);
    setSignatureImage(null);
    setCurrentStep(0);
    setSignatures({});
    setActiveFieldType(FIELD_TYPES.SIGNATURE);
    setSignerName("");
    setSavedSignature(null);
    setSavedInitial(null);
  };

  return (
    <SignatureContext.Provider
      value={{
        /* state */
        pdfFile,
        blocks,
        signatureImage,
        currentStep,
        signatures,
        activeFieldType,
        signerName,
        savedSignature,
        savedInitial,

        /* setters */
        setPdfFile,
        setBlocks,
        setSignatureImage,
        setCurrentStep,
        setSignatures,
        setActiveFieldType,
        setSignerName,
        setSavedSignature,
        setSavedInitial,

        /* helpers */
        resetSignatureContext,
        FIELD_TYPES,
      }}
    >
      {children}
    </SignatureContext.Provider>
  );
}

export function useSignature() {
  const ctx = useContext(SignatureContext);
  if (!ctx) {
    throw new Error("useSignature must be used inside SignatureProvider");
  }
  return ctx;
}
